import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';

type TasaRow = { fecha: string; tasa: number; fuente: string };

const FUENTE_TONE: Record<string, string> = {
  manual: 'bg-amber-900 text-amber-200',
  scrape: 'bg-emerald-900 text-emerald-200',
  api: 'bg-blue-900 text-blue-200',
  fallback: 'bg-blue-900 text-blue-200',
};

export function Tasa() {
  const { can } = useAuth();
  const [latest, setLatest] = useState<{ fecha: string; tasa: number; fuente: string } | null>(
    null,
  );
  const [today, setToday] = useState<string>('');
  const [series, setSeries] = useState<TasaRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mFecha, setMFecha] = useState('');
  const [mTasa, setMTasa] = useState('');
  const [mMsg, setMMsg] = useState<string | null>(null);

  function load() {
    api
      .tasaLatest()
      .then((r) => {
        setLatest(r.latest);
        setToday(r.today);
        if (!mFecha) setMFecha(r.today);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
    api
      .tasaSeries()
      // Más reciente primero.
      .then((r) => setSeries([...r.series].sort((a, b) => (a.fecha < b.fecha ? 1 : -1))))
      .catch(() => setSeries([]));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveManual(e: React.FormEvent) {
    e.preventDefault();
    if (!mFecha || !mTasa) return;
    setMMsg(null);
    try {
      await api.tasaManual(mFecha, Number(mTasa));
      setMMsg('Tasa manual guardada (no la pisará el cron).');
      load();
    } catch (err) {
      setMMsg(err instanceof Error ? err.message : 'Error');
    }
  }

  const stale = latest && today && latest.fecha < today;

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold text-slate-100">Tasa BCV</h1>
      {error && <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>}
      <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
        {latest ? (
          <>
            <div className="text-3xl font-semibold text-slate-50">Bs {latest.tasa.toFixed(4)}</div>
            <div className="mt-1 text-sm text-slate-400">
              Fecha valor {latest.fecha} · fuente <span className="font-mono">{latest.fuente}</span>
            </div>
            {stale ? (
              <div className="mt-3 rounded-lg bg-amber-950/60 px-3 py-2 text-sm text-amber-300">
                ⚠️ La tasa podría estar desactualizada (hoy {today}).
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-slate-400">Sin tasa registrada.</p>
        )}
      </div>

      {can('tasa.actualizar') && (
        <form
          onSubmit={saveManual}
          className="rounded-xl border border-slate-700 bg-slate-800/60 p-4"
        >
          <h2 className="mb-3 text-sm font-medium text-slate-200">Editar / rellenar manualmente</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-400">
              Fecha valor
              <input
                type="date"
                value={mFecha}
                onChange={(e) => setMFecha(e.target.value)}
                className="mt-1 block rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              />
            </label>
            <label className="text-xs text-slate-400">
              Tasa (Bs/USD)
              <input
                type="number"
                step="0.0001"
                value={mTasa}
                onChange={(e) => setMTasa(e.target.value)}
                className="mt-1 block w-36 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              />
            </label>
            <button className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500">
              Guardar
            </button>
          </div>
          {mMsg && <p className="mt-2 text-sm text-amber-300">{mMsg}</p>}
        </form>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
        <h2 className="mb-3 text-sm font-medium text-slate-200">
          Historial de tasas ({series.length})
        </h2>
        <div className="max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-800">
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="px-2 py-1">Fecha valor</th>
                <th className="px-2 py-1 text-right">Tasa (Bs/USD)</th>
                <th className="px-2 py-1">Fuente</th>
              </tr>
            </thead>
            <tbody>
              {series.map((r) => (
                <tr key={r.fecha} className="border-t border-slate-800">
                  <td className="px-2 py-1.5 text-slate-300">{r.fecha}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-100">
                    {r.tasa.toFixed(4)}
                  </td>
                  <td className="px-2 py-1.5">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${FUENTE_TONE[r.fuente] ?? 'bg-slate-700 text-slate-200'}`}
                    >
                      {r.fuente}
                    </span>
                  </td>
                </tr>
              ))}
              {series.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-2 py-4 text-center text-slate-500">
                    Sin tasas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
